import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { SelectDropdown, TextAreaInput, TextInput } from "../../reusables/FormElements";
import UsersTable from "../../sub_components/UsersTable";

import { ISchoolForm, ISchoolFormProps, IUserData, SchoolField } from "../../../interfaces/forms";
import { ISelectDropdownOption } from "../../../interfaces/form_elements";
import { TSchoolStatus } from "../../../interfaces/types";

import { closeModal, openModal } from "../../../store/slices/modal";

import useSaveSchool from "../../../hooks/schools/useSaveSchool";
import useInviteUsers from "../../../hooks/users/useInviteUsers";
import useGetSchoolUsers from "../../../hooks/users/useGetSchoolUsers";

import { OPSchoolsStatuses } from "../../../data/options";

const SchoolForm = (props: ISchoolFormProps) => {
    const { schoolData, selected, setSelected } = props;

    const dispatch = useDispatch();

    const requiredFields: SchoolField[] = ["dli", "name", "status", "url", "domain", "user_name", "user_email"];
    const selectFields: SchoolField[] = ["status"];

    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<ISchoolForm>({
        id: null,
        dli: { value: "", error: "" },
        name: { value: "", error: "" },
        status: { value: { value: "setup", label: "setup" }, error: "" },
        url: { value: "", error: "" },
        domain: { value: "", error: "" },
        logo_asset_name: { value: "", error: "" },
        support_email: { value: "", error: "" },
        user_name: { value: "", error: "" },
        user_email: { value: "", error: "" },
        user_phone: { value: "", error: "" },
    });
    const [users, setUsers] = useState<IUserData[]>([]);
    const [originalUsers, setOriginalUsers] = useState<IUserData[]>([]);

    const { data: usersRes, isLoading } = useGetSchoolUsers(formData.id);
    const { mutate: saveSchool } = useSaveSchool();
    const { mutate: inviteUsers } = useInviteUsers();

    useEffect(() => {
        if (schoolData) {
            const statusOption = OPSchoolsStatuses.find(status => status.value === schoolData.status);
            setFormData({
                id: schoolData.id > 0 ? schoolData.id : null,
                dli: { value: schoolData.dli, error: "" },
                name: { value: schoolData.name, error: "" },
                url: { value: schoolData.url, error: "" },
                domain: { value: schoolData.domain, error: "" },
                logo_asset_name: { value: schoolData.logo_asset_name, error: "" },
                support_email: { value: schoolData.support_email, error: "" },
                status: statusOption ? { value: statusOption, error: "" } : { value: { value: "setup", label: "Setup" }, error: "" },
                user_name: { value: schoolData.id > 0 ? "FILLED" : "", error: "" },
                user_email: { value: schoolData.id > 0 ? "FILLED" : "", error: "" },
                user_phone: { value: schoolData.id > 0 ? "FILLED" : "", error: "" },
            });
        } else {
            setFormData({
                id: null,
                dli: { value: "", error: "" },
                name: { value: "", error: "" },
                status: { value: { value: "setup", label: "Setup" }, error: "" },
                url: { value: "", error: "" },
                domain: { value: "", error: "" },
                logo_asset_name: { value: "", error: "" },
                support_email: { value: "", error: "" },
                user_name: { value: "", error: "" },
                user_email: { value: "", error: "" },
                user_phone: { value: "", error: "" },
            });
        };
        // eslint-disable-next-line
    }, [schoolData]);

    const onChangeText = (name: SchoolField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onChangeSelect = (name: SchoolField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as unknown as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };

            return !value?.trim()?.length;
        });


        const isDataSame =
            formData.dli.value === (schoolData?.dli ?? '') &&
            formData.name.value === (schoolData?.name ?? '') &&
            formData.url.value === (schoolData?.url ?? '') &&
            formData.domain.value === (schoolData?.domain ?? '') &&
            formData.logo_asset_name.value === (schoolData?.logo_asset_name ?? '') &&
            formData.support_email.value === (schoolData?.support_email ?? '') &&
            formData.status.value.value === (schoolData?.status ?? '');

        if (JSON.stringify(users) !== JSON.stringify(originalUsers)) {
            return users.some(user => !user.name?.trim()?.length || !user.email?.trim()?.length);
        };

        return emptyFields || isDataSame || isProcessing;
    };

    const isOkToInvite = (from: string): boolean => {
        // sequence matters here

        if (!isDisabled()) return false;

        if (schoolData?.status === 'setup' || schoolData?.status === 'disabled') return false;

        if (JSON.stringify(users) !== JSON.stringify(originalUsers)) {
            if (users.some(user => !user.id)) return false;
        };

        if (users.every(user => user.verified)) return false;

        return true;
    };

    const onSave = () => {
        if (isDisabled() || !selected) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id") return;
                newData[key as SchoolField].error = "";
            });
            return newData;
        });

        const getPlainPhone = (phone: string | null) => {
            return phone?.trim()?.length ? phone.replace(/\D/g, '') : null;
        };

        saveSchool({
            payload: {
                id: formData.id,
                dli: formData.dli.value,
                name: formData.name.value,
                status: formData.status.value.value as TSchoolStatus,
                url: formData.url.value,
                domain: formData.domain.value,
                logo_asset_name: formData.logo_asset_name.value,
                support_email: formData.support_email.value,
                user_name: formData.id ? null : formData.user_name.value,
                user_email: formData.id ? null : formData.user_email.value,
                user_phone: formData.id ? null : getPlainPhone(formData.user_phone.value),
            },
            usersPayload: users,
            setIsProcessing,
            selected,
            setSelected,
        });
    };

    const onDelete = () => {
        if (!selected) return;

        if (!formData.id) {
            setSelected(null);
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: "Confirm delete school?",
                content: "This will also delete all associated users.",
                actionTxt: "Delete",
                action: () => {
                    dispatch(closeModal('MESSAGE_MODAL'));
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    const onInviteUsers = (user: IUserData | null) => {
        if (!formData.id || (user && !user.id)) return;

        let modalTitle = "Confirm invite all users?";
        let modalContent = "This will send invitation emails to all unverified users associated with this school.";
        let payload = users.filter(user => user.id !== null).map(user => user.id as number);
        if (user?.id) {
            if (user.verified) return;

            modalTitle = "Confirm invite user?";
            modalContent = `This will send an invitation email to ${user.name} (${user.email}).`;
            payload = [user.id];
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: true,
                title: modalTitle,
                content: modalContent,
                actionTxt: "Invite",
                action: () => {
                    setIsProcessing(true);

                    inviteUsers({
                        payload,
                        setIsProcessing,
                    });
                },
                cancelTxt: "Cancel",
            }
        }))
    };

    return (
        <div className="immersion_admin__common_form">
            <div className="immersion_admin__common_form__inputs">
                <TextInput
                    name="dli"
                    type="text"
                    max_length={10}
                    label="DLI"
                    required={true}
                    errorMsg={formData.dli.error}
                    placeholder="Enter DLI"
                    onChange={(value: string) => onChangeText("dli", value)}
                    value={formData.dli.value}
                />
                <TextInput
                    name="name"
                    type="text"
                    max_length={50}
                    label="School Name"
                    required={true}
                    errorMsg={formData.name.error}
                    placeholder="Enter school name"
                    onChange={(value: string) => onChangeText("name", value)}
                    value={formData.name.value}
                />
                <TextAreaInput
                    name="url"
                    max_length={200}
                    label="School URL"
                    required={true}
                    errorMsg={formData.url.error}
                    placeholder="Enter school URL"
                    onChange={(value: string) => onChangeText("url", value)}
                    value={formData.url.value}
                />
                <TextInput
                    name="domain"
                    type="text"
                    max_length={50}
                    label="School Domain"
                    required={true}
                    errorMsg={formData.domain.error}
                    placeholder="Enter school domain"
                    onChange={(value: string) => onChangeText("domain", value)}
                    value={formData.domain.value}
                />
                <TextInput
                    name="logo_asset_name"
                    type="text"
                    max_length={50}
                    label="Logo Asset Name"
                    required={true}
                    errorMsg={formData.logo_asset_name.error}
                    placeholder="Enter logo asset name from S3/GCP bucket"
                    onChange={(value: string) => onChangeText("logo_asset_name", value)}
                    value={formData.logo_asset_name.value}
                />
                <TextInput
                    name="support_email"
                    type="email"
                    max_length={50}
                    label="Support Email"
                    required={true}
                    errorMsg={formData.support_email.error}
                    placeholder="Enter support email"
                    onChange={(value: string) => onChangeText("support_email", value)}
                    value={formData.support_email.value}
                />
                {!formData.id
                    ? <>
                        <TextInput
                            name="user_name"
                            type="text"
                            max_length={100}
                            label="User Name"
                            required={true}
                            errorMsg={formData.user_name.error}
                            placeholder="Enter user name"
                            onChange={(value: string) => onChangeText("user_name", value)}
                            value={formData.user_name.value}
                        />
                        <TextInput
                            name="user_email"
                            type="email"
                            max_length={100}
                            label="User Email"
                            required={true}
                            errorMsg={formData.user_email.error}
                            placeholder="Enter user email"
                            onChange={(value: string) => onChangeText("user_email", value)}
                            value={formData.user_email.value}
                        />
                        <TextInput
                            name="user_phone"
                            type="text"
                            max_length={12}
                            placeholder="650-000-0000"
                            pattern_mask="phone_number"
                            label="User Phone"
                            errorMsg={formData.user_phone.error}
                            onChange={(value: string) => onChangeText("user_phone", value)}
                            value={formData.user_phone.value}
                        />
                    </>
                    : <>
                        <SelectDropdown
                            name="status"
                            label="School Status"
                            required={true}
                            placeholder="-- select status --"
                            value={formData.status.value}
                            onChange={(option: ISelectDropdownOption) => onChangeSelect("status", option)}
                            errorMsg={formData.status.error}
                            options={OPSchoolsStatuses}
                        />
                        <br />
                        <h4 className="color_dark">Users:</h4>
                        <UsersTable
                            schoolId={formData.id}
                            usersRes={usersRes ?? []}
                            users={users}
                            setUsers={setUsers}
                            setOriginalUsers={setOriginalUsers}
                            isLoading={isLoading}
                            onInviteUsers={onInviteUsers}
                            isOkToInvite={isOkToInvite}
                        />
                        <br /><br />
                    </>
                }
            </div>
            <div className="immersion_admin__common_form__actions">
                {!formData.id &&
                    <button
                        className={`immersion_admin__btn immersion_admin__btn__secondary ${isProcessing ? "immersion_admin__btn__secondary__disabled" : ""}`}
                        onClick={() => isProcessing ? null : onDelete()}
                    >
                        {!formData.id ? 'Discard' : 'Delete'}
                    </button>
                }
                {formData.id &&
                    <button
                        className={`immersion_admin__btn immersion_admin__btn__secondary ${isOkToInvite('all') ? "" : "immersion_admin__btn__secondary__disabled"}`}
                        onClick={() => isOkToInvite('all') ? onInviteUsers(null) : null}
                    >
                        {
                            isProcessing
                                ? <i className='immersion_admin__btn_loader' />
                                : 'Invite All Users'
                        }
                    </button>
                }
                <button
                    className={`immersion_admin__btn immersion_admin__btn__primary ${isDisabled() ? "immersion_admin__btn__primary__disabled" : ""}`}
                    onClick={onSave}
                >
                    {
                        isProcessing
                            ? <i className='immersion_admin__btn_loader' />
                            : 'Save'
                    }
                </button>
            </div>
        </div>
    );
};

export default SchoolForm;